/**
 * HTML Content Processing Utilities
 *
 * This module contains utility functions for processing and formatting HTML content
 * for email composition, with special handling for dark mode compatibility.
 */

import DOMPurify from 'dompurify';
import { extractAndSanitizeEmailCSS } from './cssSanitizer';

/**
 * Secure sanitization for email HTML content
 * Uses both DOMPurify for HTML and custom sanitizer for CSS
 * @param htmlContent - Raw HTML content to sanitize
 * @param scope - Selector of the element the email renders in; the email's
 *   CSS is confined to it
 * @returns Object containing sanitized HTML and extracted CSS
 */
export const sanitizeEmailHtml = (
  htmlContent: string,
  scope: string
): { html: string; css: string } => {
  // Extract and sanitize CSS before HTML sanitization (DOMPurify will remove <style> tags)
  const sanitizedCSS = extractAndSanitizeEmailCSS(htmlContent, scope);

  const sanitized = DOMPurify.sanitize(htmlContent, {
    // Use blacklist approach - allow everything except what's explicitly forbidden
    // Security settings
    ALLOW_DATA_ATTR: false, // Prevent data-* attributes
    ALLOW_UNKNOWN_PROTOCOLS: false, // Only allow http:, https:, mailto:
    ALLOWED_URI_REGEXP:
      /^(?:(?:(?:f|ht)tps?|mailto|tel|sms):|[^a-z]|[a-z+.-]+(?:[^a-z+.-:]|$))/i,

    // Preserve email structure - these are critical for email layout
    CUSTOM_ELEMENT_HANDLING: {
      tagNameCheck: null, // Allow all tag names
      attributeNameCheck: null, // Allow all attribute names
      allowCustomizedBuiltInElements: true, // Allow customized built-in elements
    },

    // Forbidden tags (dangerous elements that can execute scripts or compromise security)
    FORBID_TAGS: [
      'script', // JavaScript execution
      'object', // Can embed plugins/flash
      'embed', // Can embed plugins/flash
      'applet', // Java applets
      'iframe', // Can load external content
      'frame', // Can load external content
      'frameset', // Frame container
      'link', // Can load external stylesheets/resources
      // Note: 'style' removed to allow legitimate CSS formatting in emails
      'meta', // Can redirect or modify page behavior
      'base', // Can change base URL for relative links
      'form', // Can submit data
      'input', // Form inputs
      'button', // Can have onclick handlers
      'select', // Form inputs
      'textarea', // Form inputs
      'option', // Form inputs
      'optgroup', // Form inputs
      'svg', // Can contain scripts
      'math', // MathML can contain scripts
    ],

    // Forbidden attributes (all JavaScript event handlers and dangerous attributes)
    FORBID_ATTR: [
      // Event handlers
      'onabort',
      'onactivate',
      'onafterprint',
      'onafterupdate',
      'onbeforeactivate',
      'onbeforecopy',
      'onbeforecut',
      'onbeforedeactivate',
      'onbeforeeditfocus',
      'onbeforepaste',
      'onbeforeprint',
      'onbeforeunload',
      'onbeforeupdate',
      'onblur',
      'onbounce',
      'oncellchange',
      'onchange',
      'onclick',
      'oncontextmenu',
      'oncontrolselect',
      'oncopy',
      'oncut',
      'ondataavailable',
      'ondatasetchanged',
      'ondatasetcomplete',
      'ondblclick',
      'ondeactivate',
      'ondrag',
      'ondragend',
      'ondragenter',
      'ondragleave',
      'ondragover',
      'ondragstart',
      'ondrop',
      'onerror',
      'onerrorupdate',
      'onfilterchange',
      'onfinish',
      'onfocus',
      'onfocusin',
      'onfocusout',
      'onhelp',
      'onkeydown',
      'onkeypress',
      'onkeyup',
      'onlayoutcomplete',
      'onload',
      'onlosecapture',
      'onmousedown',
      'onmouseenter',
      'onmouseleave',
      'onmousemove',
      'onmouseout',
      'onmouseover',
      'onmouseup',
      'onmousewheel',
      'onmove',
      'onmoveend',
      'onmovestart',
      'onpaste',
      'onpropertychange',
      'onreadystatechange',
      'onreset',
      'onresize',
      'onresizeend',
      'onresizestart',
      'onrowenter',
      'onrowexit',
      'onrowsdelete',
      'onrowsinserted',
      'onscroll',
      'onselect',
      'onselectionchange',
      'onselectstart',
      'onstart',
      'onstop',
      'onsubmit',
      'onunload',
      // Other dangerous attributes
      'action', // Form action
      // Note: Removed many layout-critical attributes from forbidden list
      // 'background' - needed for email backgrounds
      // 'cite' - can be used for styling context
      'codebase', // Can specify code location
      'dynsrc', // Dynamic source
      'formaction', // Form action
      // 'longdesc' - sometimes used for accessibility
      'lowsrc', // Low resolution source
      // 'poster' - video poster, not usually dangerous
      'profile', // Profile URL
      // 'usemap' - image map, not usually dangerous
    ],

    // CSS and styling preservation settings
    ALLOW_SELF_CLOSE_IN_ATTR: false, // Prevent self-closing in attributes
    FORBID_CONTENTS: ['script'], // Remove script content even if tag is allowed

    // Critical: Allow CSS styling for email layout preservation
    WHOLE_DOCUMENT: false, // Don't treat as full HTML document
    RETURN_DOM: false, // Return as string, not DOM
    RETURN_DOM_FRAGMENT: false, // Return as string

    // More permissive CSS handling for emails
    ADD_ATTR: ['style'], // Explicitly allow style attribute
    ADD_TAGS: ['center', 'font'], // Explicitly allow email-specific tags

    // Keep legitimate whitespace and content
    KEEP_CONTENT: true,

    // CSS XSS protection while preserving layout styles
    SANITIZE_DOM: true, // Enable DOM sanitization

    // Allow more CSS properties critical for email layouts
    // DOMPurify by default allows safe CSS properties, this ensures we don't block layout ones
  });

  return {
    html: sanitized,
    css: sanitizedCSS,
  };
};

/**
 * Processes HTML content to ensure visibility in dark mode
 * Removes problematic color and background-color styles while preserving other styles
 * @param html - Raw HTML content to process
 * @returns Processed HTML content safe for dark mode
 */
export const processHtmlContent = (html: string): string => {
  // First remove CID images using string replacement to prevent browser from trying to load them
  const processedHtml = html.replace(
    /<img[^>]*src\s*=\s*["']cid:[^"']*["'][^>]*>/gi,
    ''
  );

  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = processedHtml;

  // Remove problematic color/background-color styles, preserve other styles
  const elements = tempDiv.querySelectorAll('*');
  elements.forEach((element) => {
    const style = element.getAttribute('style') || '';
    if (style.includes('color:') || style.includes('background-color:')) {
      // Only remove color and background-color rules, preserve other styles
      const newStyle = style
        .split(';')
        .filter((rule) => !/^\s*(?:background-)?color\s*:/i.test(rule))
        .join(';');
      element.setAttribute('style', newStyle);
    }
  });

  return tempDiv.innerHTML;
};

/**
 * Rebuilds tables that an upstream sanitizer flattened into spans.
 *
 * Mail forwarded from the Google Groups web UI arrives with every
 * table/tr/td renamed to <span> while keeping the table-only attributes
 * (cellpadding, cellspacing, valign, bgcolor, ...). Those attributes are
 * meaningless on a span, so their presence is a reliable signature of the
 * rewrite — ordinary email HTML never trips this. Restores real table
 * elements so the browser lays the content out as the sender intended.
 *
 * @param html - Stored email HTML, possibly containing flattened tables
 * @returns HTML with the table structure restored (unchanged otherwise)
 */
export const restoreFlattenedTables = (html: string): string => {
  // Cheap gate: only ever rewritten tables put these attributes on spans.
  if (!/<span[^>]*cell(?:padding|spacing)\s*=/i.test(html)) {
    return html;
  }

  // Parse into an inert document: nothing in it runs or loads, so handling
  // the (still unsanitized) HTML here cannot execute a crafted payload.
  // sanitizeEmailHtml still runs on the result before anything renders.
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const root = doc.body;

  const rename = (el: Element, tagName: string): Element => {
    const replacement = doc.createElement(tagName);
    for (const attr of Array.from(el.attributes)) {
      replacement.setAttribute(attr.name, attr.value);
    }
    while (el.firstChild) {
      replacement.appendChild(el.firstChild);
    }
    el.replaceWith(replacement);
    return replacement;
  };

  // 1. Former <table>s carry cellpadding/cellspacing.
  root
    .querySelectorAll('span[cellpadding], span[cellspacing]')
    .forEach((span) => rename(span, 'table'));

  // 2. Former rows and cells inside a restored table carry presentational
  //    attributes (valign/bgcolor/align/width/height). Processed in document
  //    order, outermost first.
  const CANDIDATE =
    'span[valign], span[bgcolor], span[align], span[width], span[height]';
  const isRowLike = (span: Element): boolean => {
    // A former row tiles its cells: at least two span children with no direct
    // text content, at least one of them itself a candidate (cells may be a
    // mix of attributed and bare spans). Requiring two children keeps a cell
    // that merely wraps one presentational span a cell (a genuine single-cell
    // row then renders as a lone cell, which lays out identically).
    const children = Array.from(span.children);
    if (children.length < 2) {
      return false;
    }
    const allChildrenAreSpans = children.every(
      (child) => child.tagName === 'SPAN'
    );
    const someChildIsCandidate = children.some((child) =>
      child.matches(CANDIDATE)
    );
    if (!allChildrenAreSpans || !someChildIsCandidate) {
      return false;
    }
    return Array.from(span.childNodes).every(
      (node) => node.nodeType !== Node.TEXT_NODE || !node.textContent?.trim()
    );
  };
  root.querySelectorAll(CANDIDATE).forEach((span) => {
    // Rename only inside a restored table, and never inside a cell: a span
    // whose nearest structural ancestor is a td is cell CONTENT.
    const nearest = span.parentElement?.closest('td, table');
    if (!nearest || nearest.tagName !== 'TABLE') {
      return;
    }
    rename(span, isRowLike(span) ? 'tr' : 'td');
  });

  // 3. A cell's bare-span parent was the row...
  root.querySelectorAll('td').forEach((td) => {
    const parent = td.parentElement;
    if (parent && parent.tagName === 'SPAN') {
      rename(parent, 'tr');
    }
  });

  // ...and a row's leftover bare-span children were attribute-less cells.
  root.querySelectorAll('tr > span').forEach((span) => rename(span, 'td'));

  // 4. Unwrap leftover bare spans between a table and its rows (former
  //    tbody-level wrappers). Left in place, the HTML parser would foster-
  //    parent them out of the table and hoist the content above it.
  //    Only spans that wrap recognized structure are peeled; a span with no
  //    tr/td inside (e.g. a style-only former row) is left whole so step 5
  //    wraps it as one row and its grouping survives.
  root.querySelectorAll('table').forEach((table) => {
    // Structure belonging to a nested table does not make its wrapper a
    // tbody of THIS table - only rows/cells whose table is this one count.
    const wrapsOwnStructure = (span: Element): boolean =>
      Array.from(span.querySelectorAll('tr, td')).some(
        (el) => el.closest('table') === table
      );
    const findWrapper = () =>
      Array.from(table.children).find(
        (child) => child.tagName === 'SPAN' && wrapsOwnStructure(child)
      );
    let wrapper = findWrapper();
    while (wrapper) {
      wrapper.replaceWith(...Array.from(wrapper.childNodes));
      wrapper = findWrapper();
    }
  });

  // 5. Anything still sitting directly under the table that isn't table
  //    machinery would be foster-parented above it by the HTML parser; wrap
  //    it in a row/cell of its own so no content escapes the table.
  const TABLE_PARTS = [
    'TBODY',
    'THEAD',
    'TFOOT',
    'TR',
    'TD',
    'TH',
    'COLGROUP',
    'CAPTION',
  ];
  root.querySelectorAll('table').forEach((table) => {
    Array.from(table.childNodes).forEach((node) => {
      const isElement = node.nodeType === Node.ELEMENT_NODE;
      const isStray = isElement
        ? !TABLE_PARTS.includes((node as Element).tagName)
        : Boolean(node.textContent?.trim());
      if (!isStray) {
        return;
      }
      const row = doc.createElement('tr');
      const cell = doc.createElement('td');
      node.replaceWith(row);
      row.appendChild(cell);
      cell.appendChild(node);
    });
  });

  // DOMParser hoists leading <style> blocks into document.head; re-emit
  // them so sanitizeEmailHtml can still extract the email's CSS.
  const headStyles = Array.from(doc.head.querySelectorAll('style'))
    .map((style) => style.outerHTML)
    .join('');

  return headStyles + root.innerHTML;
};

/**
 * Picks the HTML to render for a message, or '' when it should render as text.
 *
 * Some senders put the HTML source in the text/plain part and send no HTML
 * part (seen on replies to Jotform FNOL notifications). A text body that is a
 * whole HTML document is therefore rendered as HTML. The check is anchored to
 * the start of the body so a plain-text mail that merely mentions markup
 * still renders as text.
 */
export const selectHtmlBody = (message: {
  readonly htmlBodyAll?: string;
  readonly textBodyAll?: string;
}): string => {
  if (message.htmlBodyAll?.trim()) {
    return message.htmlBodyAll;
  }
  const text = message.textBodyAll?.trim() ?? '';
  if (/^<(?:!doctype\s+html|html[\s>])/i.test(text)) {
    return text;
  }
  return '';
};

/**
 * Removes paragraphs and divs that hold nothing but whitespace or &nbsp;.
 *
 * Each character in the body may match only one way: JavaScript's `\s` already
 * includes the non-breaking space, so a pattern that also lists `\u00a0`, or
 * wraps the run in extra `\s*`, backtracks exponentially when a long run of
 * non-breaking spaces is followed by text. One such email froze the Cmail tab.
 */
export const removeEmptyBlocks = (html: string): string =>
  html
    .replace(/<p[^>]*>(?:\s|&nbsp;)*<\/p>/gi, '')
    .replace(/<div[^>]*>(?:\s|&nbsp;)*<\/div>/gi, '');

/**
 * Strips HTML tags from content while preserving newlines with \n characters
 * @param html - HTML content to strip
 * @returns Plain text content with preserved newlines
 */
export const stripHtmlTagsPreserveNewlines = (html: string): string => {
  if (!html) {
    return '';
  }

  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;

  // Replace <br>, <br/>, <br /> tags with newlines
  const brElements = tempDiv.querySelectorAll('br');
  brElements.forEach((br) => {
    br.replaceWith('\n');
  });

  // Replace <p> tags with newlines (start and end)
  const pElements = tempDiv.querySelectorAll('p');
  pElements.forEach((p) => {
    p.insertAdjacentText('beforebegin', '\n');
    p.insertAdjacentText('afterend', '\n');
  });

  // Replace <div> tags with newlines (start and end)
  const divElements = tempDiv.querySelectorAll('div');
  divElements.forEach((div) => {
    div.insertAdjacentText('beforebegin', '\n');
    div.insertAdjacentText('afterend', '\n');
  });

  // Get text content and clean up excessive newlines
  let text = tempDiv.textContent || tempDiv.innerText || '';

  // Clean up excessive newlines (more than 2 consecutive newlines become 2)
  text = text.replace(/\n{3,}/g, '\n\n');

  // Trim leading and trailing whitespace/newlines
  text = text.trim();

  return text;
};

/**
 * Creates a quoted reply message with proper formatting
 * @param originalContent - The original message content
 * @param dateReceived - Date the original message was received
 * @param senderName - Name of the original sender
 * @param senderEmail - Email of the original sender
 * @param theme - Theme object for styling
 * @returns Formatted HTML for reply message
 */
export const createReplyContent = (
  originalContent: string,
  dateReceived: string,
  senderName: string,
  senderEmail: string
): string => {
  const processedContent = originalContent
    ? processHtmlContent(originalContent)
    : originalContent;

  const contentToShow = processedContent || '<em>[No content available]</em>';

  const safeSender = textToHtml(senderName || senderEmail);
  const safeDate = textToHtml(dateReceived);

  return `<br><br>On ${safeDate}, ${safeSender} wrote:<br><div style="margin-left: 1em; border-left: 1px solid var(--cmail-notification-container-border); padding-left: 1em; color: var(--mantine-color-dimmed);">${contentToShow}</div>`;
};

/**
 * Creates a forwarded message with proper formatting
 * @param originalContent - The original message content
 * @param dateReceived - Date the original message was received
 * @param senderName - Name of the original sender
 * @param senderEmail - Email of the original sender
 * @param subject - Subject of the original message
 * @param theme - Theme object for styling
 * @returns Formatted HTML for forwarded message
 */
export const createForwardContent = (
  originalContent: string,
  dateReceived: string,
  senderName: string,
  senderEmail: string,
  subject: string
): string => {
  const processedContent = originalContent
    ? processHtmlContent(originalContent)
    : '<em>[No content available]</em>';

  const safeSender = textToHtml(senderName || senderEmail);
  const safeDate = textToHtml(dateReceived);
  const safeSubject = textToHtml(subject);

  return `<br><br>---------- Forwarded message ---------<br>From: ${safeSender}<br>Date: ${safeDate}<br>Subject: ${safeSubject}<br><br><div style="margin-left: 1em; border-left: 1px solid var(--cmail-notification-container-border); padding-left: 1em; color: var(--mantine-color-dimmed);">${processedContent}</div>`;
};

/**
 * Extracts the main content from a message object, trying multiple possible fields
 * @param message - Message object containing various content fields
 * @returns The best available content string
 */
export const extractMessageContent = (message: any): string => {
  return (
    message.htmlBodyAll ||
    message.textBodyAll ||
    message.mainMessage ||
    message.origMessage ||
    message.htmlBody ||
    message.body ||
    message.content ||
    ''
  );
};

/**
 * Sanitizes HTML content by removing potentially dangerous elements and attributes
 * @param html - HTML content to sanitize
 * @returns Sanitized HTML content
 */
export const sanitizeHtmlContent = (html: string): string => {
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;

  // Remove script tags and their content
  const scripts = tempDiv.querySelectorAll('script');
  scripts.forEach((script) => script.remove());

  // Remove potentially dangerous attributes
  const allElements = tempDiv.querySelectorAll('*');
  allElements.forEach((element) => {
    const dangerousAttributes = [
      'onclick',
      'onload',
      'onerror',
      'onmouseover',
      'onfocus',
      'onblur',
      'onchange',
      'onsubmit',
      'onkeydown',
      'onkeyup',
      'onkeypress',
    ];

    dangerousAttributes.forEach((attr) => {
      element.removeAttribute(attr);
    });
  });

  return tempDiv.innerHTML;
};

/**
 * Strips HTML tags from content, leaving only text
 * @param html - HTML content to strip
 * @returns Plain text content
 */
export const stripHtmlTags = (html: string): string => {
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;
  return tempDiv.textContent || tempDiv.innerText || '';
};

/**
 * Truncates HTML content to a specified length while preserving HTML structure
 * @param html - HTML content to truncate
 * @param maxLength - Maximum length in characters
 * @param suffix - Suffix to add when truncated (default: '...')
 * @returns Truncated HTML content
 */
export const truncateHtmlContent = (
  html: string,
  maxLength: number,
  suffix = '...'
): string => {
  const plainText = stripHtmlTags(html);

  if (plainText.length <= maxLength) {
    return html;
  }

  const truncatedText =
    plainText.substring(0, maxLength - suffix.length) + suffix;
  return truncatedText;
};

/**
 * Converts plain text to HTML with proper line breaks
 * @param text - Plain text content
 * @returns HTML content with line breaks converted to <br> tags
 */
export const textToHtml = (text: string): string => {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
    .replace(/\n/g, '<br>');
};

/**
 * Re-wraps HTML content into a full HTML document if the document-level tags
 * were stripped (e.g. by contentEditable which cannot preserve <html>/<head>/<body>).
 */
export const ensureHtmlDocument = (html: string): string => {
  const trimmed = html.trim();
  if (
    trimmed.toLowerCase().startsWith('<!doctype') ||
    trimmed.toLowerCase().startsWith('<html')
  ) {
    return trimmed;
  }

  if (!/<style[\s\S]*?>/i.test(trimmed) && !/<table[\s\S]*?>/i.test(trimmed)) {
    return trimmed;
  }

  const metaTags = trimmed.match(/<meta[^>]*\/?>/gi) || [];
  const styleTags = trimmed.match(/<style[\s\S]*?<\/style>/gi) || [];
  const headTags = [...metaTags, ...styleTags];

  const bodyContent = trimmed
    .replace(/<meta[^>]*\/?>/gi, '')
    .replace(/<style[\s\S]*?<\/style>/gi, '')
    .trim();

  const headInner =
    headTags.length > 0 ? `\n    ${headTags.join('\n    ')}\n` : '\n';

  return `<!DOCTYPE html>\n<html lang="en">\n<head>${headInner}</head>\n<body>\n${bodyContent}\n</body>\n</html>`;
};
